import React from 'react';
import { Link } from 'react-router-dom';

import useDocumentTitle from '../hooks/useDocumentTitle';
import { LANDING_META } from '../config/publicSite';
import { TAGLINE } from '../config/brand';

/**
 * keepr's landing page (KPR-316): what keepr is, who it is for and how it
 * works, for a visitor with no account. `/` signed out, in the public shell
 * (pages/RootFrame.js).
 *
 * TWO WAYS IN, ONE PAGE. The app renders this component when someone signed
 * out reaches `/` inside it (signing out, a refused token). A visitor who
 * types keepr.cloud reads the same component as static HTML, rendered at build
 * time by scripts/prerender-public.mjs, with no app loaded at all — the app's
 * bundle is far too heavy for a front door. So this page must stay plain:
 *
 *   - no data, no API call, no state, no effect but the tab title;
 *   - links, not handlers: every way out works with JavaScript off;
 *   - styles only from css/components/public-site.css (plus tokens, themes and
 *     the brand rules), which the prerender inlines — never an antd
 *     component, whose styles exist only once the app runs.
 *
 * THE WORDS are drafted from the user guides (getting-started, create an
 * account, assistants) and docs/keepr-brief.md, in the product's vocabulary,
 * and say nothing about plans or price (the owner's ruling, KPE-008). The
 * owner approves them in review.
 *
 * THE PICTURES are the real app: the public share pages rendered with demo
 * records and photographed in both modes (scripts/landing-shots/README.md
 * says how to retake them). The light and dark copies swap on `data-mode`, and load
 * lazily, so only the one on screen is fetched.
 */

// Each screenshot exists in light and dark at two widths, as AVIF with a PNG
// fallback: public/landing/<name>-<mode>-<width>.<ext>.
const SHOT_WIDTHS = [640, 1280];
// The hero is one column below 960px (public-site.css), with 16px gutters on
// a phone and 24px above; beside the words it is about 560px wide.
const SHOT_SIZES = '(max-width: 767px) calc(100vw - 32px), (max-width: 959px) calc(100vw - 48px), 560px';

function Shot({ name, alt, caption, width, height }) {
	const srcSet = (mode, ext) => SHOT_WIDTHS.map(w => `/landing/${name}-${mode}-${w}.${ext} ${w}w`).join(', ');
	return (
		<figure className="landing__figure">
			<div className="landing__frame">
				{['light', 'dark'].map(mode => (
					<picture key={mode} className={`landing__shot landing__shot--${mode}`}>
						<source type="image/avif" srcSet={srcSet(mode, 'avif')} sizes={SHOT_SIZES} />
						<img
							src={`/landing/${name}-${mode}-${SHOT_WIDTHS[SHOT_WIDTHS.length - 1]}.png`}
							srcSet={srcSet(mode, 'png')}
							sizes={SHOT_SIZES}
							alt={alt}
							width={width}
							height={height}
							loading="lazy"
							decoding="async"
						/>
					</picture>
				))}
			</div>
			{caption ? <figcaption className="landing__caption">{caption}</figcaption> : null}
		</figure>
	);
}

const AUDIENCES = [
	{
		title: 'At home',
		text: 'One calm place for the things you keep track of: a household inventory, a reading log, plant care, medical history, a car\'s service record.',
	},
	{
		title: 'For a project or a hobby',
		text: 'Deeper records for deeper interests: a home lab, a breeding program, a workshop, a competition season, a research notebook.',
	},
	{
		title: 'For a small business',
		text: 'Records for the work, without a heavy system: a clinic\'s visits, a studio\'s bookings, a fleet\'s inspections, shared so the right people see the right cards.',
	},
];

const STEPS = [
	{
		title: 'Make a collection',
		text: 'A collection is one area of your life or work: a home, a garden, a clinic, a fleet. Everything you keep about it lives there, with its own sharing and settings. Every account starts with a personal collection.',
	},
	{
		title: 'Describe a card',
		text: 'A card is the template for one kind of record, built from elements with real data types: text, choices, dates, ratings, measurements with units, files, links to other items, and values calculated by a formula.',
	},
	{
		title: 'Add items',
		text: 'Each item is one record. keepr draws its form, its place in a list you can filter, and its read view from the card, and keeps a history of every change.',
	},
];

// The hero's picture is a collection's list; the two below the steps are a
// card and one of its items. The alt text says what is in each, for someone
// who cannot see it.
const HERO_SHOT = {
	name: 'items',
	alt: 'A household inventory in keepr: its items as tiles, each with the room it is in, its price and its condition',
	width: 1280,
	height: 800,
};

const SHOTS = [
	{
		name: 'card',
		alt: 'A card in keepr, listing each of its elements with its data type',
		caption: 'A card: the elements every item of its kind carries.',
		width: 1280,
		height: 800,
	},
	{
		name: 'item',
		alt: 'One item in keepr, read as a record: its room, brand, price, warranty date, serial number, condition and notes',
		caption: 'An item: one record, read the way its card lays it out.',
		width: 1280,
		height: 800,
	},
];

const PROMISES = [
	{ title: 'Every change on the record', text: 'Each item keeps its history: who changed which field, from what, to what, and when. A change can be reverted.' },
	{ title: 'Find anything', text: 'Filter any list and save the filter. Press ⌘K, or Ctrl+K, to jump back to what you opened recently.' },
	{ title: 'Share on your terms', text: 'Share a collection with a person or a group, down to one card. Keep an item private, or send a public link that expires.' },
	{ title: 'Take it with you', text: 'Export an item or a list to PDF or JSON, and a list to Excel too, whenever you like.' },
	{ title: 'Let it run', text: 'Automations fill in elements, update matching items and tell the right people when something changes.' },
	{ title: 'On your phone', text: 'Every page works on a phone, down to scanning a barcode straight into a field.' },
];

const ASSISTANT_RULES = [
	'It checks what a collection accepts before it reads or writes, and never guesses an element or a value.',
	'You see every row before anything is added, and every change to a card as a preview keepr itself made.',
	'It can never share a collection, delete one, or act as an administrator.',
];

export default function Landing() {
	useDocumentTitle(LANDING_META.title);

	return (
		<div className="landing">
			<section className="landing__hero" aria-labelledby="landing-title">
				<div className="landing__hero-text">
					<h1 id="landing-title" className="landing__title">{TAGLINE}</h1>
					<p className="landing__lead">
						keepr is one place for the records you care about, in the shape you choose. Describe a kind
						of record once, and keepr gives you a form to fill it in, a list to find it, a history to
						trust it, and ways to share, export and automate it.
					</p>
					<div className="landing__actions">
						<Link to="/register" className="public-button public-button--primary public-button--large">Sign up</Link>
						<Link to="/login" className="public-button public-button--large">Sign in</Link>
					</div>
					<p className="landing__aside">
						Works on your phone, and with Claude, ChatGPT or a coding agent.
					</p>
				</div>
				<div className="landing__hero-shot">
					<Shot {...HERO_SHOT} />
				</div>
			</section>

			<section className="landing__section" aria-labelledby="landing-who">
				<h2 id="landing-who" className="landing__heading">For whatever you keep track of</h2>
				<p className="landing__intro">
					Spreadsheets bend until they break, notes have no structure, and most apps track only the one
					thing their maker imagined. In keepr, you decide what a record is.
				</p>
				<ul className="landing__grid landing__grid--three">
					{AUDIENCES.map(audience => (
						<li key={audience.title} className="landing__panel">
							<h3 className="landing__panel-title">{audience.title}</h3>
							<p className="landing__panel-text">{audience.text}</p>
						</li>
					))}
				</ul>
			</section>

			<section className="landing__section" aria-labelledby="landing-how">
				<h2 id="landing-how" className="landing__heading">How keepr works</h2>
				<p className="landing__intro">Three ideas carry the whole product: collections, cards and items.</p>
				<ol className="landing__steps">
					{STEPS.map((step, index) => (
						<li key={step.title} className="landing__step">
							<span className="landing__step-number" aria-hidden="true">{index + 1}</span>
							<h3 className="landing__step-title">{step.title}</h3>
							<p className="landing__step-body">{step.text}</p>
						</li>
					))}
				</ol>
				<div className="landing__shots">
					{SHOTS.map(shot => <Shot key={shot.name} {...shot} />)}
				</div>
			</section>

			<section className="landing__section" aria-labelledby="landing-trust">
				<h2 id="landing-trust" className="landing__heading">Records you can trust</h2>
				<ul className="landing__grid landing__grid--three">
					{PROMISES.map(promise => (
						<li key={promise.title} className="landing__panel">
							<h3 className="landing__panel-title">{promise.title}</h3>
							<p className="landing__panel-text">{promise.text}</p>
						</li>
					))}
				</ul>
			</section>

			<section className="landing__section landing__assistants" aria-labelledby="landing-assistants">
				<h2 id="landing-assistants" className="landing__heading">Bring your assistant</h2>
				<p className="landing__intro">
					Claude, ChatGPT or a coding agent can read, add and change records in your collections. It
					connects through keepr&apos;s connector: there is no key to copy, and you choose the collections
					it may reach before you press Allow.
				</p>
				<ul className="landing__rules">
					{ASSISTANT_RULES.map(rule => <li key={rule}>{rule}</li>)}
				</ul>
				<p className="landing__more">
					<Link to="/docs/guides/assistants/index">Read how to connect an assistant</Link>
				</p>
			</section>

			<section className="landing__section landing__closing" aria-labelledby="landing-start">
				<h2 id="landing-start" className="landing__heading">Start with your personal collection</h2>
				<p className="landing__intro">
					Every account comes with a personal collection, a welcome note and four to dos to show you
					around.
				</p>
				<div className="landing__actions">
					<Link to="/register" className="public-button public-button--primary public-button--large">Sign up</Link>
					<Link to="/docs" className="public-button public-button--large">Read the docs</Link>
				</div>
			</section>
		</div>
	);
}
